import api, { SESSION_EXPIRED_EVENT } from "@/lib/api";
import { UserRead } from "@openapi/models";
import { App } from "antd";
import { usePathname, useRouter } from "next/navigation";
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useEffectEvent,
    useRef,
    useState,
} from "react";

interface AuthContextType {
    isLoggedIn: boolean;
    user?: UserRead;
    loading: boolean;
    login: () => Promise<void>;
    logout: (onSuccess?: () => void) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Pure fetch of the current user's profile via the cookie-authenticated
 * proxy. Performs no state mutations; throws on failure so callers can
 * decide how to react.
 */
const fetchUserProfile = async (): Promise<UserRead> => {
    return await api.users.getCurrentUserProfile();
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [user, setUser] = useState<UserRead | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const { message, notification } = App.useApp();
    const router = useRouter();
    const pathname = usePathname();

    // Ref mirrors `isLoggedIn` so the global 401 listener (registered once)
    // can read the latest value without re-subscribing on every state change.
    const isLoggedInRef = useRef(isLoggedIn);
    useEffect(() => {
        isLoggedInRef.current = isLoggedIn;
    }, [isLoggedIn]);

    // Initial auth check on mount. Does NOT call the backend logout endpoint
    // on failure — the user wasn't authenticated to begin with, so there is
    // nothing to log out from. We just clear local state.
    useEffect(() => {
        let cancelled = false;
        const initializeAuth = async () => {
            try {
                const userProfile = await fetchUserProfile();
                if (cancelled) return;
                setUser(userProfile);
                setIsLoggedIn(true);
            } catch {
                // Expected when there is no session cookie, or a stale one
                // (the proxy clears it on the 401). The 401 also dispatches
                // SESSION_EXPIRED_EVENT, which handleUnauthorized ignores
                // while not logged in, so only this catch acts on it.
                if (cancelled) return;
                setUser(undefined);
                setIsLoggedIn(false);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        void initializeAuth();
        return () => {
            cancelled = true;
        };
    }, []);

    // Called by the login page after a successful credentials submission to
    // populate the user profile and flip the auth state. Does not call the
    // backend logout endpoint on failure — the credentials call already
    // succeeded; if the immediately-following profile fetch fails, something
    // unusual is happening, so we log it and clear local state only.
    const login = useCallback(async () => {
        setLoading(true);
        try {
            const userProfile = await fetchUserProfile();
            setUser(userProfile);
            setIsLoggedIn(true);
        } catch (error) {
            console.error("Failed to fetch user profile after login:", error);
            setUser(undefined);
            setIsLoggedIn(false);
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(
        async (onSuccess?: () => void) => {
            try {
                // Call logout API (clears httpOnly cookie via proxy)
                await api.auth.logout();
            } catch (error) {
                message.error("Failed to log out");
                console.error("Logout error:", error);
            } finally {
                setIsLoggedIn(false);
                setUser(undefined);
                setLoading(false);
                if (onSuccess) {
                    onSuccess();
                }
            }
        },
        [message],
    );

    const handleUnauthorized = useEffectEvent(() => {
        if (!isLoggedInRef.current) {
            return;
        }
        setIsLoggedIn(false);
        setUser(undefined);
        notification.warning({
            title: "Session expired",
            description: "Please log in again.",
            placement: "bottomRight",
        });
        const redirect = pathname ? encodeURIComponent(pathname) : "";
        router.replace(redirect ? `/login?redirect=${redirect}` : "/login");
    });

    // A 401 on any call while logged in means the backend no longer knows
    // the session (lib/api.ts dispatches the event). The proxy has already
    // cleared the cookie, so only the local state and the page remain.
    useEffect(() => {
        window.addEventListener(SESSION_EXPIRED_EVENT, handleUnauthorized);
        return () => {
            window.removeEventListener(
                SESSION_EXPIRED_EVENT,
                handleUnauthorized,
            );
        };
    }, []);

    return (
        <AuthContext.Provider
            value={{ isLoggedIn, user, loading, login, logout }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
